/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import PropTypes from 'prop-types';
import React, { forwardRef, memo, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { displayFlex, flexCol, flexRow, parseWidth, positionRelative, typographyTypes } from "../../styles/general";
import { classNames } from "../../utils";
import TabContext from "./context";
const TabContainer = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(({
  action = {},
  children,
  className,
  direction,
  level,
  style,
  tabIndex,
  width,
  ...props
}, reference) => {
  if (!reference) reference = useRef(null);
  const ref = useRef(null);
  const {
    value
  } = props;
  const [tabIndexState, setTabIndexState] = useState(tabIndex || value);
  if (level === 'level1') level = 'h3';
  if (level === 'level2') level = 'p1';
  const _TabContainerCSS = TabContainerCSS(direction, width);
  useEffect(() => {
    if (typeof value === 'number') setTabIndexState(value);else setTabIndexState(tabIndex);
  }, [value, tabIndex]);
  useImperativeHandle(reference, () => {
    const currentRef = ref.current || {};
    currentRef.element = ref.current;
    const _instance = {
      ...action
    }; // methods
    _instance.__proto__ = {}; // hidden methods
    currentRef.instance = _instance;
    return currentRef;
  });
  return useMemo(() => {
    return jsx("div", {
      css: _TabContainerCSS,
      ref: ref,
      className: classNames('DGN-UI-Tab-Container', className),
      style: style,
      ...props
    }, jsx(TabContext.Provider, {
      value: {
        tabIndexState,
        setTabIndexState,
        direction
      }
    }, React.Children.map(children, child => child && /*#__PURE__*/React.cloneElement(child, {
      level
    }))));
  }, [children, className, direction, level, props, style, tabIndex, width, tabIndexState, value]);
}));
const TabContainerCSS = (direction, width) => css`
    ${displayFlex};
    ${direction === 'vertical' ? flexRow : flexCol};
    ${positionRelative};
    ${parseWidth(width)};
`;
TabContainer.defaultProps = {
  className: '',
  direction: 'horizontal',
  level: 'level1',
  style: {},
  tabIndex: 0,
  width: '100%'
};
TabContainer.propTypes = {
  /** The content of the component. */
  children: PropTypes.node,
  /** Class for component */
  className: PropTypes.string,
  /** Direction change the position of Tabs. */
  direction: PropTypes.oneOf(['vertical', 'horizontal']),
  /** Direction change the size typography of Tab (typesTypography is types of Typography - exp: h1, h2, h3, p1 ...). */
  level: PropTypes.oneOfType([PropTypes.oneOf(['level1', 'level2', ...typographyTypes])]),
  /** Style inline of component. */
  style: PropTypes.object,
  /** The value of the currently selected Tab. */
  tabIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** Width of component. */
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** Any props else. */
  props: PropTypes.any
};
export default TabContainer;